import { useDispatch } from "react-redux";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import clsx from "clsx";
import { setModalxComponent, setModalxComponentProps } from "components/modalx/modalx.slice";
import ProductSidebar from "./product-sidebar";
import { useProductList } from "./use-product-list";

/** What the product on the right can ask of the page around it. */
export interface ProductsOutletContext {
  /** Fetch the sidebar's first page again, after a product was edited. */
  reloadList: () => void;
}

/**
 * The company's products and services: a searchable list on the left and, once
 * one is picked, the product named in the address (`products/:productId`) on
 * the right. On a narrow screen only one of the two shows at a time.
 */
export default function Products() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { companyId = "", productId } = useParams();
  const list = useProductList(companyId);

  const handleCreate = () => {
    dispatch(setModalxComponent("ProductForm"));
    dispatch(
      setModalxComponentProps({
        onSubmit: (createdId: string) => {
          list.reload();
          navigate(`/app/${companyId}/products/${createdId}`);
        },
      })
    );
  };

  const outletContext: ProductsOutletContext = { reloadList: list.reload };

  return (
    <div className="flex h-[calc(100dvh-var(--dashboard-topbar-height,4rem)-var(--phone-bar-height,0px))] bg-gray-50">
      <ProductSidebar
        companyId={companyId}
        selectedId={productId}
        list={list}
        onCreate={handleCreate}
        className={productId ? "hidden lg:flex" : "flex"}
      />
      <main className={clsx("min-w-0 flex-1 overflow-y-auto", !productId && "hidden lg:block")}>
        {productId ? (
          <>
            <Link
              to={`/app/${companyId}/products`}
              className="flex min-h-11 items-center gap-1.5 px-4 text-sm text-indigo-600 hover:underline lg:hidden"
            >
              <ArrowLeft aria-hidden className="h-4 w-4" />
              {t("products_page_back_to_list")}
            </Link>
            <Outlet context={outletContext} />
          </>
        ) : (
          <p className="p-8 text-sm text-gray-500">{t("products_page_pick_one")}</p>
        )}
      </main>
    </div>
  );
}
